<template>
  <div v-if="!loading">
    <Breadcrumbs />

    <div ref="product-details" class="product container">
      <component :is="photoGallery" />

      <div class="content">
        <div class="product__details">
          <div class="title__wrapper title__wrapper--flex">
            <div class="title">
              <div v-if="isOnSale" class="product__onsale">SALE</div>
              <h1 class="product__title">{{ product.title }}</h1>
            </div>
            <div class="buttons">
              <WishlistButton
                class="wishlist-button"
                data-testid="wishlist-button-pdp"
                :product="product"
              />
              <ShareButton />
            </div>
          </div>

          <p v-if="product.shortDescription" class="product__description">
            {{ product.shortDescription.value }}
          </p>
          <h2 class="exclusive">Available exclusively at Rat & Boa</h2>
          <div ref="addToBag">
            <AddToBag />
          </div>

          <div v-if="showBNPL" class="product__payment">
            <BNPLInformation :price="variant?.priceV2" />
          </div>

          <IconText
            v-if="showDeliveryEstimateComponent"
            icon="shipping"
            class="enhanced-edd"
          >
            <DeliveryEstimatorNoInput
              class="estimate-delivery-date"
              show-countdown
            />
          </IconText>

          <IconText icon="returns"> 30 Day Returns On All Items </IconText>

          <div
            class="wysiwyg product__full-description"
            v-html="product.descriptionHtml"
          ></div>

          <div class="additional-details">
            <Accordion
              v-if="product?.fabricAndPrintDescription"
              class="product__accordion"
              show-arrow
              open-by-default
            >
              <template #header>Fabric &amp; Print</template>
              <template #body>
                <FabricAndPrint />
              </template>
            </Accordion>
            <Accordion
              v-if="product?.stylingDescription"
              class="product__accordion"
              show-arrow
            >
              <template #header>Styling</template>
              <template #body>
                <Styling />
              </template>
            </Accordion>
            <Accordion
              v-if="product?.aftercareDescription"
              class="product__accordion"
              show-arrow
            >
              <template #header>Aftercare</template>
              <template #body>
                <Aftercare />
              </template>
            </Accordion>
            <Accordion class="product__accordion" show-arrow>
              <template #header>Shipping &amp; Returns</template>
              <template #body>
                <ShippingAndReturns />
              </template>
            </Accordion>
          </div>

          <HowToWear
            v-if="showHowToWear && !mq.mdPlus"
            :references="howToWearReferences"
          />
        </div>
      </div>
    </div>

    <div class="container">
      <CompleteTheSet :product-id="productId" />
    </div>

    <div v-if="showHowToWear && mq.mdPlus" class="container">
      <HowToWear :references="howToWearReferences" />
    </div>

    <NostoProductRecommendations
      v-if="nostoProductRecommendationsProduct"
      :recommendations="nostoProductRecommendationsProduct"
      :placement="nostoProductRecommendationsProductId"
      heading="You might also like"
    />
    <NostoProductRecommendations
      v-if="nostoProductRecommendationsBehaviour"
      :recommendations="nostoProductRecommendationsBehaviour"
      :placement="nostoProductRecommendationsBehaviourId"
      heading="An edit just for you"
    />

    <div class="js-shopify-placement">
      <ProductRecommendations :product-id="product.id" flat-lays />
    </div>

    <Transition name="fade">
      <StickyAddToBag v-if="showStickyAddToBag" />
    </Transition>

    <SizeGuide v-model:open="isSizeGuideOpen" :sizes="sizeGuide" />
  </div>
  <ProductSkeleton v-else />
</template>

<script>
import cloneDeep from "lodash-es/cloneDeep";
import { mapActions, mapGetters, mapState } from "vuex";
import NostoProductRecommendations from "@/components/NostoProductRecommendations.vue";
import AddToBag from "@/components/product-display/AddToBag.vue";
import BNPLInformation from "@/components/product-display/BNPLInformation.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInput.vue";
import ProductRecommendations from "@/components/product-display/ProductRecommendations.vue";
import ProductSkeleton from "@/components/skeleton/ProductSkeleton.vue";
import { regionFeatureToggles } from "@/config.js";
import productQuery from "@/graphql/queries/Product.gql";
import { viewProductEvent } from "@/helpers/events.js";
import { unwrapNodes } from "@/helpers/graphql";
import { capitalizeFirstLetter } from "@/helpers/string-manipulation.js";
import { getParams } from "@/helpers/url.js";
import ProductGalleryDesktop from "@/components/product-display/ProductGalleryDesktop.vue";
import ProductGallery from "@/components/product-display/ProductGallery.vue";
import ShippingAndReturns from "@/components/product-display/ShippingAndReturns.vue";
import Aftercare from "@/components/product-display/Aftercare.vue";
import Styling from "@/components/product-display/Styling.vue";
import FabricAndPrint from "@/components/product-display/FabricAndPrint.vue";
import Accordion from "@/components/Accordion.vue";
import StickyAddToBag from "@/components/product-display/StickyAddToBag.vue";
import SizeGuide from "@/components/product-display/SizeGuide.vue";
import ShareButton from "@/components/product-display/ShareButton.vue";
import WishlistButton from "@/components/WishlistButton.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import IconText from "@/components/IconText.vue";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import HowToWear from "@/components/product-display/HowToWear.vue";
import CompleteTheSet from "@/components/product-display/CompleteTheSet.vue";

export default {
  name: "ProductPage",
  components: {
    ShippingAndReturns,
    Aftercare,
    Styling,
    FabricAndPrint,
    ProductGalleryDesktop,
    ProductGallery,
    AddToBag,
    BNPLInformation,
    ProductRecommendations,
    NostoProductRecommendations,
    ProductSkeleton,
    DeliveryEstimatorNoInput,
    StickyAddToBag,
    SizeGuide,
    ShareButton,
    WishlistButton,
    Breadcrumbs,
    Accordion,
    IconText,
    HowToWear,
    CompleteTheSet,
  },
  inject: ["mq"],
  props: {
    productHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      product: null,
      variantBySelectedOptions: null,
      productId: null,
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsBehaviourId: null,
      referredPlacement: null,
      loading: true,
      showStickyAddToBag: false,
      loadedNoProductData: false,
    };
  },
  computed: {
    ...mapState("product", [
      "selectedOptions",
      "selectedVariant",
      "defaultVariant",
      "openSizeGuide",
    ]),
    ...mapGetters("product", ["selectedOptionsWithoutId"]),
    ...mapGetters("app", ["currencyCode"]),
    variant() {
      return this.selectedVariant || this.defaultVariant;
    },
    photoGallery() {
      if (this.mq.mdPlus) {
        return ProductGalleryDesktop;
      }
      return ProductGallery;
    },
    hasMultipleVariants() {
      const options = this.product.options;
      return options.some((option) => option.values.length > 1);
    },
    showBNPL() {
      if (this.variant) {
        return true;
      }

      return false;
    },
    price() {
      const price = this.variant.priceV2;
      return this.$filters.toCurrency(price.amount, price.currencyCode, 0);
    },
    instagramTiles() {
      const metafields = unwrapNodes(this.product.metafields?.edges) || [];

      const instagramMetafields = metafields
        .filter((metafield) => metafield.key.includes("instagram"))
        .filter((metafield) => !!metafield?.reference?.image?.largeMedia);

      return instagramMetafields.map((ig) => {
        return {
          imageUrl: ig.reference.image.largeMedia,
        };
      });
    },
    howToWearReferences() {
      return unwrapNodes(this.product.howToWear?.references?.edges) || [];
    },
    showHowToWear() {
      return this.howToWearReferences.length > 0;
    },
    instagramReels() {
      return unwrapNodes(this.product.instagramReels?.references?.edges) || [];
    },
    selectedVariantWeight() {
      return this.selectedVariant?.weight || 0.2;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
    originalPrice() {
      return this.product.compareAtPriceRange.minVariantPrice;
    },
    isOnSale() {
      const price = this.variant.priceV2;
      return parseFloat(this.originalPrice.amount) > parseFloat(price.amount);
    },
    showDeliveryEstimateComponent() {
      return this.isEDDEnabled && this.variant.availableForSale;
    },
    sizeGuide() {
      const sizes = [];
      const productVariants = unwrapNodes(this.product.variants?.edges) || [];
      productVariants.forEach((variant) => {
        const metafields = cloneDeep(variant.sizes);

        const notNullMetafields = metafields.filter(
          (metafield) =>
            !(typeof metafield === "undefined" || metafield === null)
        );

        const metafieldsSorted = notNullMetafields.sort((a, b) =>
          a.key > b.key ? 1 : -1
        );

        const title = variant.title.split(" / ")[0];

        const size = {
          title,
          measurements: metafieldsSorted,
        };
        sizes.push(size);
      });

      return sizes;
    },
    isSizeGuideOpen: {
      get() {
        return this.openSizeGuide;
      },
      set(val) {
        this.setOpenSizeGuide(val);
      },
    },
  },
  watch: {
    productId(val) {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewProduct(val)
          .setRef(this.productId, this.referredPlacement)
          .setPlacements(["productpage-nosto-1", "productpage-nosto-2"])
          .load()
          .then((data) => {
            this.nostoProductRecommendationsProduct =
              data.recommendations["productpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["productpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["productpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["productpage-nosto-2"]?.result_id || null;
          });
      });

      viewProductEvent(this.product);

      this.setRecentlyViewedProducts(this.product.id);
    },
  },
  created() {
    this.params = getParams(window.location);
    this.referredPlacement = this.params["ref-placement"];
  },
  methods: {
    ...mapActions("product", [
      "setSelectedOptions",
      "setProduct",
      "setSelectedVariant",
      "setDefaultVariant",
      "setOpenSizeGuide",
    ]),
    ...mapActions("app", ["setRecentlyViewedProducts"]),
    handleIntersection(entries) {
      if (this.mq.mdPlus) {
        return;
      }

      let addToBagVisible = false;
      let productDetailsVisible = false;

      entries.forEach((entry) => {
        if (entry.target === this.$refs.addToBag) {
          // Intersection Observer for addToBag
          if (entry.boundingClientRect.top < 0) {
            addToBagVisible = !entry.isIntersecting;
          }
        } else if (entry.target === this.$refs["product-details"]) {
          // Intersection Observer for product-details
          this.productDetailsIntersecting = entry.isIntersecting;
          productDetailsVisible = entry.isIntersecting;
        }
      });

      this.showStickyAddToBag = addToBagVisible || productDetailsVisible;
    },
  },
  apollo: {
    product: {
      query: productQuery,
      skip() {
        return !this.productHandle;
      },
      variables() {
        const { productVersion = 1 } = getFeatureToggles();

        if (this.loadedNoProductData) {
          return {
            product: this.productHandle,
            selectedOptions: this.selectedOptionsWithoutId || [],
          };
        }

        return {
          product:
            productVersion !== 1
              ? `${this.productHandle}-v${productVersion}`
              : this.productHandle,
          selectedOptions: this.selectedOptionsWithoutId || [],
        };
      },
      result({ data }) {
        if (!data || !data.product) {
          this.loadedNoProductData = true;
          return;
        }

        this.loading = false;

        this.productId = data?.product.id.split("Product/")[1];

        this.setProduct(data?.product);

        if (this.selectedOptions.length === 0) {
          const options = data?.product.options;
          const optionsTitles = options.map((option) =>
            option.name.toLowerCase()
          );

          const actualOptions = Object.keys(this.params).filter((param) =>
            optionsTitles.includes(param)
          );

          const selectedOptions = [];
          actualOptions.forEach((name) => {
            const value = this.params[name];

            const { id } = options.find(
              (option) => option.name.toLowerCase() === name
            );

            selectedOptions.push({
              id,
              name: capitalizeFirstLetter(name),
              value: value.toUpperCase(),
            });
          });

          this.setSelectedOptions(selectedOptions);
        }

        this.$nextTick(() => {
          const combinedObserver = new IntersectionObserver(
            this.handleIntersection
          );

          combinedObserver.observe(this.$refs.addToBag);
          combinedObserver.observe(this.$refs["product-details"]);
        });

        const variants = unwrapNodes(data?.product.variants?.edges);

        const variantByOptions = data?.product.variantBySelectedOptions;
        if (variantByOptions) {
          this.setSelectedVariant(data?.product.variantBySelectedOptions);
        } else {
          const firstAvailableForSaleVariant = variants.find(
            (variant) => variant.availableForSale
          );

          if (firstAvailableForSaleVariant) {
            this.setDefaultVariant(firstAvailableForSaleVariant);
            return;
          }

          this.setSelectedVariant(variants[0]);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product {
  @include respond-above(md) {
    display: grid;
    grid-template-columns: 3fr minmax(624px, 2fr);
  }

  &__onsale {
    color: #070707;
    font-size: 13px;
    margin: 0 0 5px 0;
    opacity: 0.3;
  }

  &__title {
    @include t__editorial-headline-20;
  }

  &__description {
    @include t__editorial-body-10;
    margin: 10px 0 20px 0;
  }

  &__details {
    padding: 30px 20px 0;

    @include respond-above(md) {
      padding: 80px;
    }
  }

  &__full-description {
    margin-top: 30px;

    p {
      @include t__utility-body-20;
    }
  }

  &__payment {
    margin-top: 16px;
    font-size: 12px;
  }

  .additional-details {
    margin: 30px 0;
  }

  &__accordion {
    border-bottom: 1px solid #d4d4d4;

    ::v-deep(.accordion__header) {
      background: none;
      outline: none;
      border: none;
      padding: 12px 0;
      width: 100%;
      text-align: left;
      @include t__utility-label-30;
    }

    ::v-deep(.accordion__body) {
      display: block;
      font-family: $body-font-family;
      font-size: 13px;
      height: auto;
      padding-bottom: 14px;
    }

    ::v-deep(.accordion__arrow) {
      font-size: 16px;
    }
  }
}

.product.container {
  @include respond-below(md) {
    padding-left: 0;
    padding-right: 0;
  }
}

.content {
  @include respond-above(md) {
    // position: sticky;
    // top: 85px;
    align-self: start;
  }
}

.wysiwyg ::v-deep() {
  font-size: 12px;

  p {
    margin-bottom: 15px;
  }

  ul,
  ol {
    list-style: initial;
    margin-bottom: 15px;

    li {
      margin-left: 15px;
      @include t__utility-body-20;
    }
  }

  *:last-child {
    margin-bottom: 0;
  }
}

.text-link {
  cursor: pointer;
  font-weight: 600;
}

.js-shopify-placement {
  display: none;
}

::v-deep(.tabs-component-tab-a) {
  font-size: 12px;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translateY(100%);
}

.exclusive {
  @include t__utility-message-30;
  margin: 0 0 20px 0;
}

.wishlist-button {
  padding-right: 0 !important;

  ::v-deep(.icon) {
    font-size: 24px !important;
  }
}

.title__wrapper {
  margin-bottom: 10px;

  &--flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0px;
  }
}

.social-buttons {
  display: flex;
  margin-top: 15px;
  gap: 10px;
}

.estimate-delivery-date {
  text-align: left;
  margin-top: 0;

  ::v-deep(.label) {
    display: block;
    text-transform: none;
    font-weight: 400;
    margin-bottom: 4px;
  }
}

.enhanced-edd {
  margin-top: 2px;
}
</style>
